import { BACKEND_URL } from "./lib/backend";
/**
 * BriefArchivePage — Public SEO-indexed archive of all published briefs.
 * Route: /briefs
 * Each brief links to /travel-brief/:seo_slug for the full content.
 */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Archive, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import PublicPageNav from "./components/PublicPageNav";
import PublicPageFooter from "./components/PublicPageFooter";

const API = `${BACKEND_URL}/api`;
const SITE = "https://tourismintel.ai";

const setMeta = (name, content, prop = false) => {
  const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  const el = document.querySelector(sel);
  if (el) el.setAttribute("content", content);
};

const fmt = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "";

export default function BriefArchivePage() {
  const [briefs, setBriefs] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Brief Archive — Travel Intelligence | TourismIntel";
    setMeta("description", "Browse all past TourismIntel Travel Intelligence Briefs — weekly strategic analysis of AI, innovation, and sustainability in the travel industry.");
    setMeta("og:title", "Brief Archive | TourismIntel", true);
    setMeta("og:url", `${SITE}/briefs`, true);
  }, []);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/briefs?page=${page}&limit=12`)
      .then((r) => {
        setBriefs(r.data.briefs || []);
        setPages(r.data.pages || 1);
        setTotal(r.data.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <div style={{ background: "#f8f9fa", minHeight: "100vh", fontFamily: "'Space Grotesk', sans-serif" }}>

      {/* Nav — shared across all public pages */}
      <PublicPageNav active="archive" />

      {/* Hero */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e8ecee", padding: "48px 24px 36px" }}>
        <div style={{ maxWidth: 960, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Archive style={{ width: 16, height: 16, color: "#006874" }} />
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#6b7a7d" }}>
              Brief Archive
            </span>
          </div>
          <h1 style={{ margin: 0, fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 700, letterSpacing: "-0.03em", color: "#191c1d", lineHeight: 1.1 }}>
            Travel Intelligence Briefs
          </h1>
          <p style={{ margin: "12px 0 0", fontSize: 16, color: "#6b7a7d", maxWidth: 540, lineHeight: 1.6 }}>
            Weekly strategic analysis of AI, innovation and sustainability in the global travel industry — curated and written by Mirko Lalli.
          </p>
          <p style={{ margin: "16px 0 0", fontSize: 12, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: "#bac9cc" }}>
            {total} {total === 1 ? "brief" : "briefs"} published
          </p>
        </div>
      </div>

      {/* Grid */}
      <main style={{ maxWidth: 960, margin: "0 auto", padding: "48px 24px 96px" }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "80px 0" }}>
            <RefreshCw style={{ width: 24, height: 24, color: "#006874", animation: "spin 1s linear infinite" }} />
          </div>
        ) : briefs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: "#6b7a7d" }}>
            <Archive style={{ width: 40, height: 40, margin: "0 auto 16px", opacity: 0.3 }} />
            <p style={{ fontSize: 18, fontWeight: 600 }}>No published briefs yet</p>
            <p style={{ fontSize: 14, marginTop: 8 }}>Check back soon.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
            {briefs.map((b) => {
              const linkSlug = b.seo_slug || b.slug;
              const displayTitle = b.title || `Travel Intelligence — ${b.slug}`;
              return (
                <Link
                  key={b.slug}
                  to={`/travel-brief/${linkSlug}`}
                  data-testid={`archive-brief-${b.slug}`}
                  style={{
                    display: "flex", flexDirection: "column", textDecoration: "none",
                    padding: "24px", borderRadius: 16, background: "#fff",
                    border: "1px solid #e8ecee",
                    transition: "border-color 0.15s, box-shadow 0.15s",
                    boxShadow: "0 2px 8px rgba(25,28,29,0.04)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#006874";
                    e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,104,116,0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e8ecee";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(25,28,29,0.04)";
                  }}
                >
                  <p style={{ margin: "0 0 10px", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#006874" }}>
                    {fmt(b.generated_at)}
                  </p>
                  <h2 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700, letterSpacing: "-0.02em", color: "#191c1d", lineHeight: 1.35, flexGrow: 1 }}>
                    {displayTitle}
                  </h2>
                  {b.preview && (
                    <p style={{
                      margin: "0 0 16px", fontSize: 13, color: "#6b7a7d", lineHeight: 1.6,
                      display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
                    }}>
                      {b.preview}
                    </p>
                  )}
                  <span style={{
                    fontSize: 12, fontWeight: 600, color: "#006874", letterSpacing: "-0.01em",
                    display: "inline-flex", alignItems: "center", gap: 4,
                  }}>
                    Read brief →
                  </span>
                </Link>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 56 }}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 99,
                border: "1px solid #e8ecee", background: page <= 1 ? "#f3f4f5" : "#fff",
                color: page <= 1 ? "#bac9cc" : "#191c1d", cursor: page <= 1 ? "not-allowed" : "pointer",
                fontSize: 13, fontWeight: 600, transition: "border-color 0.15s",
              }}
            >
              <ChevronLeft style={{ width: 14, height: 14 }} /> Previous
            </button>
            <span style={{ fontSize: 13, color: "#6b7a7d" }}>Page {page} of {pages}</span>
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 99,
                border: "1px solid #e8ecee", background: page >= pages ? "#f3f4f5" : "#fff",
                color: page >= pages ? "#bac9cc" : "#191c1d", cursor: page >= pages ? "not-allowed" : "pointer",
                fontSize: 13, fontWeight: 600, transition: "border-color 0.15s",
              }}
            >
              Next <ChevronRight style={{ width: 14, height: 14 }} />
            </button>
          </div>
        )}
      </main>

      {/* Footer */}
      <PublicPageFooter />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
